import React, { useEffect, useRef, useState } from 'react';

import OptionItem from '@/components/ui/ComboBox/OptionItem';
import { EnumLike } from '@/types';
import cn from '@/utils/cn';

import { ScrollArea } from '../Select/ScrollArea';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';

// wysokość jednej opcji (padding + line-height) - punkt startowy, docelowa
// wysokość jest mierzona po zamontowaniu, więc zawijające się etykiety są ok
const ESTIMATED_OPTION_HEIGHT = 30;
// odpowiednik gap-1 z niewirtualizowanej listy
const OPTION_GAP = 4;
// zapas nad i pod viewportem - musi być większy niż 1, żeby Headless UI zawsze
// miało zarejestrowaną kolejną opcję do podświetlenia strzałkami
const OVERSCAN = 12;

interface Props {
  options: EnumLike[];
  fieldName: string;
  selectedValue: string;
  mah: string;
  withScrollbarSpace?: boolean;
}

const VirtualOptionList: React.FC<Props> = ({
  options,
  fieldName,
  selectedValue,
  mah,
  withScrollbarSpace,
}) => {
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const isFirstRender = useRef(true);

  const selectedIndex = options.findIndex(
    (item) => item.value === selectedValue
  );

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => viewport,
    estimateSize: () => ESTIMATED_OPTION_HEIGHT,
    getItemKey: (index) => options[index].value,
    gap: OPTION_GAP,
    overscan: OVERSCAN,
    // Headless UI widzi tylko zamontowane opcje, więc zawsze montujemy:
    // - wybraną - inaczej po odscrollowaniu traci na niej zaznaczenie,
    // - pierwszą i ostatnią - Home/PageUp i End/PageDown skaczą do pierwszej
    //   i ostatniej zarejestrowanej opcji, a nie do krańców całej listy
    rangeExtractor: (range) => {
      // przy pustej liście options.length - 1 === -1, ale nie polegamy na tym,
      // że rodzic nie renderuje listy bez wyników
      if (options.length === 0) return [];
      const pinned = [0, options.length - 1, selectedIndex].filter(
        (index) => index >= 0
      );
      return Array.from(
        new Set([...defaultRangeExtractor(range), ...pinned])
      ).sort((a, b) => a - b);
    },
  });

  useEffect(() => {
    if (!viewport) return;

    // przy otwarciu listy pokazujemy wybraną opcję, przy zmianie wyników
    // wyszukiwania wracamy na początek
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (selectedIndex >= 0) {
        virtualizer.scrollToIndex(selectedIndex, { align: 'center' });
      }
      return;
    }
    virtualizer.scrollToOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport, options]);

  return (
    <ScrollArea mah={mah} viewportRef={setViewport}>
      <div className={cn('border-gray-400', withScrollbarSpace && 'pr-3.5')}>
        {/* in order to not set first value */}
        <OptionItem
          item={{ value: '', label: '' }}
          key={'empty'}
          className={cn('hidden')}
          data-testid={`option-${fieldName}-empty`}
        />
        <div
          className="relative w-full"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const item = options[virtualItem.index];
            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                className="absolute left-0 top-0 w-full"
                style={{ transform: `translateY(${virtualItem.start}px)` }}
              >
                <OptionItem
                  item={item}
                  data-testid={`option-${fieldName}-${item.value}`}
                  className={cn(
                    item.value === selectedValue && 'font-semibold'
                  )}
                />
              </div>
            );
          })}
        </div>
      </div>
    </ScrollArea>
  );
};

export default VirtualOptionList;
